import React, { useState } from 'react';
import DataTable from 'react-data-table-component';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { showConfirm, showInfo } from '../../../../utills/sweetAlert';
import { useSelector } from 'react-redux';
import { hasPermission } from '../../../../utills/hasPermission';

const cardStyle = {
  background: 'transparent',
  border: 'none',
  boxShadow: 'none',
  padding: 0,
  minWidth: 0,
  marginBottom: 0,
  fontFamily: 'Poppins, sans-serif',
};

const headerStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: 18,
};

const titleStyle = {
  fontWeight: 700,
  fontSize: 18,
  color: '#111',
  letterSpacing: 0.7,
};

const newBtnStyle = {
  background: 'linear-gradient(135deg, #00C853 0%, #43A047 100%)',
  color: '#fff',
  border: 'none',
  borderRadius: 8,
  padding: '10px 26px',
  fontWeight: 700,
  fontSize: 16,
  cursor: 'pointer',
  marginRight: 12,
  letterSpacing: 0.5,
  transition: 'all 0.3s ease',
  boxShadow: '0 2px 8px rgba(0, 200, 83, 0.3)',
};

const searchContainer = {
  position: 'relative',
  minWidth: 200,
  marginLeft: 8,
};

const searchInput = {
  width: 200,
  padding: '10px 36px 10px 38px',
  border: '1.5px solid #C8E6C9',
  borderRadius: 8,
  fontSize: 16,
  color: '#1B5E20',
  background: '#fff',
  fontFamily: 'inherit',
  outline: 'none',
  transition: 'border 0.3s',
};

const searchIconStyle = {
  position: 'absolute',
  left: 12,
  top: '50%',
  transform: 'translateY(-50%)',
  color: '#00C853',
  fontSize: 20,
  pointerEvents: 'none',
};

const actionStyle = {
  cursor: 'pointer',
  marginRight: 10,
  background: 'none',
  border: 'none',
  borderRadius: 6,
  padding: 4,
  color: '#757575',
  transition: 'all 0.3s ease',
};

const columnHeaderStyle = {
  fontFamily: 'Poppins, sans-serif',
  fontSize: 15,
  fontWeight: 600,
  color: '#111',
};
const cellStyle = {
  fontFamily: 'Poppins, sans-serif',
  fontSize: 15,
  color: '#222',
};

const RoleTable = ({ roles, onEdit, onDelete, onNew }) => {
  const [search, setSearch] = useState('');
  const [hoveredAction, setHoveredAction] = useState({});

  // Get permissions and roleName from Redux
  const permissions = useSelector(state => state.rolePermissions.list);
  const roleName = useSelector(state => state.login.user?.role_name);

  const canAdd = hasPermission(permissions, roleName, 'role', 'add');
  const canEdit = hasPermission(permissions, roleName, 'role', 'edit');
  const canDelete = hasPermission(permissions, roleName, 'role', 'delete');

  const handleEdit = (role) => {
    showInfo('Edit Role', `Editing role: ${role.role_name}`);
    onEdit(role);
  };

  const handleDelete = async (id, role_name) => {
    const confirmed = await showConfirm('Delete Role', `Are you sure you want to delete role: ${role_name}?`, 'Delete', 'Cancel');
    if (confirmed) onDelete(id);
  };

  const filteredRoles = roles.filter(r =>
    r.role_name && r.role_name.toLowerCase().includes(search.toLowerCase())
  );

  const columns = [
    {
      name: 'Role Name',
      selector: row => row.role_name,
      sortable: true,
      style: cellStyle,
      headerStyle: columnHeaderStyle,
      width: '600px',
    },
    {
      name: 'Actions',
      cell: row => (
        <>
          {canEdit && (
            <button
              style={actionStyle}
              onClick={() => handleEdit(row)}
              title="Edit"
              onMouseEnter={() => setHoveredAction({ id: row.role_id, type: 'edit' })}
              onMouseLeave={() => setHoveredAction({})}
            >
              <EditIcon fontSize="small" />
              {hoveredAction.id === row.role_id && hoveredAction.type === 'edit' && (
                <span style={{ marginLeft: 4, color: '#1B5E20', fontWeight: 600 }}>Edit</span>
              )}
            </button>
          )}
          {canDelete && (
            <button
              style={actionStyle}
              onClick={() => handleDelete(row.role_id, row.role_name)}
              title="Delete"
              onMouseEnter={() => setHoveredAction({ id: row.role_id, type: 'delete' })}
              onMouseLeave={() => setHoveredAction({})}
            >
              <DeleteOutlineIcon fontSize="small" />
              {hoveredAction.id === row.role_id && hoveredAction.type === 'delete' && (
                <span style={{ marginLeft: 4, color: '#d32f2f', fontWeight: 600 }}>Delete</span>
              )}
            </button>
          )}
        </>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      style: cellStyle,
      headerStyle: columnHeaderStyle,
    },
  ];

  return (
    <div style={cardStyle}>
      <style>{`
        /* Custom horizontal scrollbar for the table */
        .role-table ::-webkit-scrollbar {
          height: 6px;
          background: #f2f2f2;
        }
        .role-table ::-webkit-scrollbar-thumb {
          background: #d3d3d3;
          border-radius: 4px;
        }
        .role-table {
          scrollbar-color: #d3d3d3 #f2f2f2;
          scrollbar-width: thin;
        }
      `}</style>
      <div style={headerStyle}>
        <span style={titleStyle}>Roles</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {canAdd && (
            <button style={newBtnStyle} onClick={onNew}>+ New</button>
          )}
          <div style={searchContainer}>
            <input
              style={searchInput}
              type="text"
              placeholder="Search..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            <span style={searchIconStyle}><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#00C853" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg></span>
          </div>
        </div>
      </div>
      <div className="role-table">
        <DataTable
          columns={columns}
          data={filteredRoles}
          pagination
          highlightOnHover
          striped
          fixedHeader
          fixedHeaderScrollHeight="420px"
          noDataComponent={<div style={{ color: '#888', fontSize: 16 }}>No Roles found.</div>}
          customStyles={{
            headRow: { style: { background: '#E8F5E9', fontWeight: 700 } },
            rows: { style: { minHeight: 48 } },
          }}
        />
      </div>
    </div>
  );
};

export default RoleTable; 